/* eslint-disable max-len */
/* eslint-disable indent */
import "./ChatAssistantDropdown2.css";
import { useEffect, useRef, forwardRef, useState } from "react";
import { Tooltip } from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GlobalVariables from "../models/globalVariables";
import {
  getWhiteLabeledAssistantName,
  truncateAssistantName,
} from "../helpers/whiteLabelAssistantName";

const ChatAssistantDropdown2 = forwardRef((props, ref) => {
  // Get props
  const referencedElementId = props.referencedElementId;
  const displayPosition = props.displayPosition;
  const smallContainer = props?.smallContainer || false;
  const marginRightV = props?.marginRight || "";
  const overlayMode = props.overlayMode || false;
  const portalMode = props.portalMode === true;

  const assistants =
    props.assistants && props.assistants.length
      ? props.assistants.map((assistant) => {
          if (assistant && assistant.appName)
            assistant["name"] = assistant.appName;
          if (assistant && assistant.appTitle)
            assistant["title"] = assistant.appTitle;
          if (assistant && !assistant.appID && assistant["name"])
            assistant["appID"] = assistant["name"];
          return assistant;
        })
      : [];
  const selectedAssistant = props.selectedAssistant;
  if (
    selectedAssistant &&
    !selectedAssistant.appID &&
    selectedAssistant["name"]
  )
    selectedAssistant["appID"] = selectedAssistant["name"];

  const defaultAssistant = props.defaultAssistant;
  const hideTabs = props.hideTabs;
  const hideAvatar = props.hideAvatar;
  const assistantName = props.assistantName || "";

  const assistantsDropdown = useRef(null);
  const [selectedTab, setSelectedTab] = useState("all");
  const [savingOrgDefault, setSavingOrgDefault] = useState(false);
  const sampleEmojis = ["🧑🏼‍⚕️", "👨🏽‍💼", "👩🏼‍💼", "👨🏼‍💼", "🧑🏽‍💼"];

  // Function to display dropdown under the referenced element
  const displayDropdownUnder = () => {
    // eslint-disable-next-line no-undef
    const refElement = document.getElementById(referencedElementId);
    const dropdown = assistantsDropdown.current;

    if (refElement && dropdown) {
      const rect = refElement.getBoundingClientRect();
      let top = rect.bottom;
      const left = 0;

      // Modify top value based on displayPosition
      if (displayPosition === "top") {
        top = rect.top - dropdown.offsetHeight;
      }

      dropdown.style.position = "absolute";
      dropdown.style.top = `${top + 5}px`;
      if (marginRightV === "") {
        dropdown.style.left = `${left}px`;
        dropdown.style.right = ``;
      } else {
        dropdown.style.left = ``;
        dropdown.style.right = `${left}px`;
      }
    }
    if (dropdown && overlayMode && props.numberOfMessagesInChat > 0) {
        // Get the height of the dropdown
        const dropdownHeight = dropdown.offsetHeight + 10;
        // Get the element with class assistants inside the dropdown
        const assistantsElement = dropdown.querySelector(".assistants");
        const actualAssistantHeight = assistantsElement.offsetHeight;
        const pageHeightInPixels = window.innerHeight;
        const newTop = pageHeightInPixels - actualAssistantHeight - 140;
        // Get the parent element of the dropdown
        const parentElement = dropdown.parentElement;
        parentElement.style.top = `${newTop}px`;
    }
  };

  const appOrigin = props.createNewLink || GlobalVariables.getEmbeddingAppOrigin();

  const redirectToNewAssistant = () => {
    window.open(`${appOrigin}/aiassistants`, "_blank");
  };

  // const modifyAssistantsFormat = (assistants) => {
  //     // Modify the assistant object to include the required fields for display
  //     const modifiedAssistants = assistants.map((assistant) => {
  //         if (assistant && assistant.appName) assistant["name"] = assistant.appName;
  //         if (assistant && assistant.appTitle) assistant["title"] = assistant.appTitle;
  //         return assistant;
  //     });
  //     return modifiedAssistants;
  // };

  // Call the displayDropdownUnder function when the component mounts
  useEffect(() => {
    displayDropdownUnder();
  }, []); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    displayDropdownUnder();
  }, [props]); // Reload component on selectedTab change
  useEffect(() => {}, [defaultAssistant]); // Reload component on defaultAssistant change

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
  };

  const onAssistantClick = (assistant) => {
    props.onAssistantClick(assistant);
  };

  const getAssistantId = (assistant) => {
    if (!assistant) return null;
    return typeof assistant === "string" ? assistant : assistant.appID;
  };

  const organizationDefaultAssistantId = getAssistantId(
    props.organizationDefaultAssistant
  );
  const selectedAssistantId = getAssistantId(selectedAssistant);
  const personalDefaultAssistantId = getAssistantId(defaultAssistant);
  const canManageOrganizationDefaultAssistant =
    props.canManageOrganizationDefaultAssistant === true &&
    typeof props.updateOrganizationDefaultAssistant === "function";

  const getAssistantPriority = (assistant, originalIndex) => {
    if (assistant?.appID && assistant.appID === selectedAssistantId) return 0;
    if (
      assistant?.appID &&
      assistant.appID === organizationDefaultAssistantId
    ) {
      return 1;
    }
    return originalIndex + 2;
  };

  const orderedAssistants = assistants
    .map((assistant, index) => ({ assistant, index }))
    .sort((a, b) => {
      const priorityA = getAssistantPriority(a.assistant, a.index);
      const priorityB = getAssistantPriority(b.assistant, b.index);
      return priorityA - priorityB;
    })
    .map(({ assistant }) => assistant);

  const getVisibleAssistants = () => {
    if (selectedTab === "default") {
      return orderedAssistants.filter(
        (assistant) => (assistant.appType || "").toLowerCase() === "default"
      );
    }

    if (selectedTab === "enterprise") {
      return orderedAssistants.filter(
        (assistant) => (assistant.appType || "").toLowerCase() !== "default"
      );
    }

    return orderedAssistants;
  };

  const handleOrgDefaultClick = async (assistant, isOrganizationDefault, e) => {
    e.stopPropagation();
    if (!assistant.trained) {
      window.open(
        `${appOrigin}/aiassistants?assistant=${assistant.appID}&tab=training&folder=files`,
        "_blank"
      );
      return;
    }

    if (!props.updateOrganizationDefaultAssistant) return;
    setSavingOrgDefault(true);
    await props.updateOrganizationDefaultAssistant(
      isOrganizationDefault ? null : assistant
    );
    setSavingOrgDefault(false);
  };

  const renderDefaultActions = (assistant, keyPrefix) => {
    if (portalMode) return null;

    const isOrganizationDefault =
      assistant?.appID && assistant.appID === organizationDefaultAssistantId;
    const isPersonalDefault =
      assistant?.appID && assistant.appID === personalDefaultAssistantId;

    return (
      <div className="assistant-default-actions">
        <Tooltip
          key={`make-default-assistant-${keyPrefix}`}
          title={
            isPersonalDefault
              ? "Clear personal default"
              : "Set as personal default"
          }
          arrow={false}
        >
          <img
            className="star-image"
            width="15"
            height="15"
            onClick={(e) => {
              e.stopPropagation();
              if (assistant.trained) {
                props.updateDefaultAssistant(assistant, e);
              } else {
                window.open(
                  `${appOrigin}/aiassistants?assistant=${assistant.appID}&tab=training&folder=files`,
                  "_blank"
                );
              }
            }}
            src={
              isPersonalDefault
                ? "/star-yellow-filled.png"
                : "/star-yellow-outline.png"
            }
          />
        </Tooltip>

        {canManageOrganizationDefaultAssistant && (
          <Tooltip
            key={`make-org-default-assistant-${keyPrefix}`}
            title={
              isOrganizationDefault
                ? "Organization default agent"
                : "Set as organization default"
            }
            arrow={false}
          >
            <button
              type="button"
              disabled={savingOrgDefault}
              className={`org-default-agent-button ${
                isOrganizationDefault ? "org-default-agent-button-active" : ""
              }`}
              onClick={(e) =>
                handleOrgDefaultClick(assistant, isOrganizationDefault, e)
              }
            >
              <BusinessOutlinedIcon fontSize="inherit" />
            </button>
          </Tooltip>
        )}
      </div>
    );
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const getAssistantDisplayName = (assistant) =>
    getWhiteLabeledAssistantName(assistant, assistantName);

  const getRenderedAssistantName = (assistant) => {
    const displayName = getAssistantDisplayName(assistant);

    if (selectedTab !== "all") {
      return truncateAssistantName(displayName, 31);
    }

    if (displayName.length <= 30) {
      return capitalizeFirstLetter(displayName);
    }

    return `${capitalizeFirstLetter(displayName.substring(0, 30))}...`;
  };

  const getAssistantDescription = (assistant) => {
    if (!assistant?.trained) return "Assistant is not trained yet";
    return assistant.title || assistant.description || "";
  };

  const renderAssistantBadges = (assistant) => {
    const isSelected = assistant?.appID && assistant.appID === selectedAssistantId;
    const isOrganizationDefault =
      assistant?.appID && assistant.appID === organizationDefaultAssistantId;
    const isPersonalDefault =
      assistant?.appID && assistant.appID === personalDefaultAssistantId;

    if (!isSelected && !isOrganizationDefault && !isPersonalDefault) return null;

    return (
      <div className="assistant-state-badges">
        {isSelected && <span className="assistant-state-badge">Selected</span>}
        {isOrganizationDefault && (
          <span className="assistant-state-badge assistant-state-badge-org">
            Org default
          </span>
        )}
        {isPersonalDefault && (
          <span className="assistant-state-badge assistant-state-badge-personal">
            Personal default
          </span>
        )}
      </div>
    );
  };

  const renderAssistantAvatar = (assistant) => {
    if (hideAvatar) return null;

    return (
      <img
        alt=""
        className="assistant-avatar-image"
        width="32"
        height="32"
        style={{
          width: 32,
          height: 32,
          objectFit: "cover",
          borderRadius: "100%",
          marginTop: 4,
        }}
        src={assistant?.imageUrl || "https://i.imgur.com/BbyEMeR.jpeg"}
      />
    );
  };

  const renderAssistantRow = (assistant, index) => {
    const isSelected =
      selectedAssistantId && assistant?.appID === selectedAssistantId;

    return (
      <Tooltip
        key={`tooltip-${selectedTab}-${assistant?.appID || index}`}
        title={
          assistant.trained
            ? ""
            : "This assistant is not trained yet. Click to train it in Addy AI."
        }
        arrow={!assistant.trained}
        placement="top"
      >
        <div
          style={{
            display: "flex",
            opacity: assistant.trained ? 1 : 0.5,
            filter: assistant.trained ? "grayscale(0%)" : "grayscale(100%)",
          }}
          className={`item1-wrapper ${isSelected ? "selected" : ""}`}
          key={assistant?.appID || index}
          onClick={(e) => {
            e.stopPropagation();
            if (assistant.trained) {
              onAssistantClick(assistant);
            } else {
              window.open(
                `${appOrigin}/aiassistants?assistant=${assistant.appID}&tab=training&folder=files`,
                "_blank"
              );
            }
          }}
        >
          {renderAssistantAvatar(assistant)}
          <div className="item1">
            <div className={`frame-group`}>
              <div className="frame-wrapper">
                <div className="h2-heading-parent16">
                  <b className="addy-assistant-name h2-heading37">
                    {getRenderedAssistantName(assistant)}
                  </b>
                  {renderAssistantBadges(assistant)}
                </div>
              </div>
              {assistant.organizationName && (
                <div
                  className={`infinite-shelf-container ${
                    smallContainer ? "infinite-shelf-container-small" : ""
                  }`}
                />
              )}
            </div>
            <div className="it-helps-you-answer-clients-q-parent">
              <div className="it-helps-you">
                <div className="item-row2">
                  <div className="it-helps-you item-description">
                    {getAssistantDescription(assistant)}
                  </div>
                  {renderDefaultActions(assistant, selectedTab)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Tooltip>
    );
  };

  const visibleAssistants = getVisibleAssistants();

  return (
    <section ref={assistantsDropdown} className="frame-section" style={overlayMode ? {
        minWidth: "500px"
    }: {}}>
      <div className="text-field4">
        <div className="assistants-container" ref={ref}>
          <div className="assistants">
            <img className="plus-icon5" alt="" src="/plus.svg" />
            <div className="chat-with-your-assistants1">
              <div className="h2-heading29">{props.headerText}</div>
              {!portalMode && <div className="create-new-wrapper">
                <p
                  onClick={() => redirectToNewAssistant()}
                  href={`${props.createNewLink ? props.createNewLink : ""}`}
                  target="_blank"
                  className="create-new"
                >
                  + Create New
                </p>
              </div>}
            </div>
            <div className="frame-parent">
              {!hideTabs && !portalMode && (
                <div className="heading-parent">
                  <div className="heading3">
                    <div
                      className={`heading4 ${
                        smallContainer ? "heading4-small" : ""
                      }`}
                    >
                      <div
                        className={`heading5 ${
                          selectedTab === "all" ? "active" : ""
                        }`}
                      >
                        <div
                          className={"h2-heading30"}
                          onClick={() => handleTabClick("all")}
                        >
                          All
                        </div>
                      </div>
                      <div
                        className={`heading5 ${
                          selectedTab === "default" ? "active" : ""
                        }`}
                      >
                        <div
                          data-toggle="tooltip"
                          title="Free assistants provided by Addy"
                          className={"h2-heading30"}
                          onClick={() => handleTabClick("default")}
                        >
                          Free
                        </div>
                      </div>

                      <div
                        className={`heading5 ${
                          selectedTab === "enterprise" ? "active" : ""
                        }`}
                      >
                        <div
                          data-toggle="tooltip"
                          title="Custom assistants created by you or your organization"
                          className={"h2-heading30"}
                          onClick={() => handleTabClick("enterprise")}
                        >
                          Enterprise
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <img
                                    className="search-icon"
                                    loading="lazy"
                                    alt=""
                                    src="/search.svg"
                                /> */}
                </div>
              )}
              <div className="item-parent addy-simple-vertical-scrollbar">
                {visibleAssistants.map((assistant, index) =>
                  renderAssistantRow(assistant, index)
                )}

                {assistants.length === 0 && (
                  <div className="loading-text">No assistants found</div>
                )}
                {!hideTabs &&
                  selectedTab === "enterprise" &&
                  visibleAssistants.length == 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <br />
                      <br />
                      <br />
                      <div className="loading-text">
                        No custom assistants found &nbsp;&nbsp;&nbsp;&nbsp;
                      </div>
                      <div className="create-new-wrapper">
                        <p
                          onClick={() => redirectToNewAssistant()}
                          href={`${
                            props.createNewLink ? props.createNewLink : ""
                          }`}
                          target="_blank"
                          className="create-new"
                        >
                          + Create New
                        </p>
                      </div>
                    </div>
                  )}

                {/* {selectedTab !== "default" &&
                                    <div className="createButtonDiv">
                                        <LoadingButton
                                            onClick={() => redirectToNewAssistant()}
                                            className={`button10`}
                                            startIcon={
                                                <img
                                                    width="18px"
                                                    height="18px"
                                                    background-color="white"
                                                    src="/electric_bolt_AI.svg"
                                                />
                                            }
                                            variant="contained"
                                            sx={{
                                                "&:hover": {
                                                    background:
                                                        "linear-gradient(92deg, #a388f3, #ab7ff1 45.5%, #ab83eb)",
                                                },
                                                background:
                                                    "linear-gradient(92deg, #a388f3, #ab7ff1 45.5%, #ab83eb)",
                                            }}
                                        >
                                            Create
                                        </LoadingButton>
                                    </div>
                                } */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default ChatAssistantDropdown2;
